import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getJob, getPremiumJob, getJobQueue, getHairAnalysisJob, getMakeupAnalysisJob } from '../api/endpoints';
import type { JobStatus } from '../types/api';

const STORAGE_KEY = 'kivo:jobs:v1';
const MAX_TRACKED = 5;

export type TrackedJobKind = 'base' | 'premium' | 'hair' | 'makeup';

export interface TrackedJob {
  id: string;
  kind: TrackedJobKind;
  submittedAt: string;
  status: JobStatus;
  queue?: number | null;
  lastCheckedAt?: string;
  // user-facing label, e.g. "Face scan", or "Premium scan"
  label?: string;
}

export interface JobsContextValue {
  jobs: TrackedJob[];
  registerJob: (input: { id: string; kind: TrackedJobKind; label?: string }) => void;
  markViewed: (id: string) => void;
  refreshStatuses: () => Promise<void>;
  isTracked: (id: string) => boolean;
}

const JobsContext = createContext<JobsContextValue | null>(null);

function load(): TrackedJob[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as TrackedJob[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_TRACKED);
  } catch {
    return [];
  }
}

function save(jobs: TrackedJob[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs.slice(0, MAX_TRACKED)));
  } catch {
    /* quota / unavailable — best-effort */
  }
}

export const JobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<TrackedJob[]>(() => load());
  const inflight = useRef(false);

  useEffect(() => {
    save(jobs);
  }, [jobs]);

  const registerJob = useCallback<JobsContextValue['registerJob']>(({ id, kind, label }) => {
    setJobs((prev) => {
      if (prev.some((j) => j.id === id)) return prev;
      const next: TrackedJob = {
        id,
        kind,
        submittedAt: new Date().toISOString(),
        status: 'queued',
        label,
      };
      return [next, ...prev].slice(0, MAX_TRACKED);
    });
  }, []);

  const markViewed = useCallback<JobsContextValue['markViewed']>((id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const refreshStatuses = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const snapshot = jobs;
      if (snapshot.length === 0) return;
      const updates = await Promise.all(
        snapshot.map(async (j) => {
          try {
            if (j.kind === 'premium') {
              const r = await getPremiumJob(j.id);
              return { id: j.id, status: r.job.status, queue: undefined };
            }
            if (j.kind === 'hair') {
              const r = await getHairAnalysisJob(j.id);
              return { id: j.id, status: r.job.status, queue: undefined };
            }
            if (j.kind === 'makeup') {
              const r = await getMakeupAnalysisJob(j.id);
              return { id: j.id, status: r.job.status, queue: undefined };
            }
            const r = await getJob(j.id);
            let queue: number | null | undefined;
            if (r.status === 'queued' || r.status === 'processing') {
              try {
                const q = await getJobQueue(j.id);
                queue = q.queue;
              } catch {
                queue = undefined;
              }
            }
            return { id: j.id, status: r.status, queue };
          } catch {
            return { id: j.id };
          }
        }),
      );
      const now = new Date().toISOString();
      setJobs((prev) =>
        prev.map((j) => {
          const u = updates.find((x) => x.id === j.id);
          if (!u) return j;
          return {
            ...j,
            status: (u.status) ?? j.status,
            queue: u.queue ?? j.queue,
            lastCheckedAt: now,
          };
        }),
      );
    } finally {
      inflight.current = false;
    }
  }, [jobs]);

  const isTracked = useCallback((id: string) => jobs.some((j) => j.id === id), [jobs]);

  const value = useMemo<JobsContextValue>(
    () => ({ jobs, registerJob, markViewed, refreshStatuses, isTracked }),
    [jobs, registerJob, markViewed, refreshStatuses, isTracked],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
};

export function useJobs(): JobsContextValue {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error('useJobs must be used within JobsProvider');
  return ctx;
}
